import { type ApolloCache, type DocumentNode, type Reference, useMutation } from '@apollo/client';
import { useState } from 'react';

import type { EngagementGovernanceDecision } from '@/__generated__/graphql';
import { ERROR_CODES, firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { SUBMIT_GOVERNANCE_DECISION } from './operations';

interface DecisionExtras {
  proposedNteTotal?: { cents: string; currency: string };
  note?: string;
}

interface GovernanceDecisionOptions {
  engagementId: string;
  /**
   * What the calling surface reads the engagement from. Re-read after a
   * decision, and after a stale one, so the surface shows the engagement as it
   * now is.
   */
  refetchQueries: DocumentNode[];
  /** The engagement no longer awaits this approver, decided here or elsewhere first. */
  onDecided?: () => void;
}

/**
 * Queue membership is the server-computed `myGovernanceQueue` array, which the
 * mutation's selection set cannot touch. Taking the row out here keeps the
 * cached queue true whether or not it is mounted: a decision made on the Hub
 * has no queue query to refetch, and without this the next visit to Approvals
 * paints the decided row and the old count until the network answers.
 */
function dropFromQueue(cache: ApolloCache<unknown>, engagementId: string) {
  cache.modify({
    fields: {
      myGovernanceQueue(existing: readonly Reference[] = [], { readField }) {
        return existing.filter((row) => readField('id', row) !== engagementId);
      },
    },
  });
}

/**
 * `submitGovernanceDecision` for one engagement, shared by the Approvals row and
 * the Hub's governance banner (ENG-5752) so the two cannot handle an outcome
 * differently.
 *
 * `pending` is the decision in flight. It is held until the surface has
 * re-read the engagement, not just until the mutation settles; see `reread`.
 */
export function useGovernanceDecision({
  engagementId,
  refetchQueries,
  onDecided,
}: GovernanceDecisionOptions) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<EngagementGovernanceDecision | null>(null);
  const [submitDecision, { client }] = useMutation(SUBMIT_GOVERNANCE_DECISION);

  /**
   * Run after the mutation, not as its `refetchQueries`. Apollo starts those
   * refetches before it broadcasts the mutation's own write, and it does not
   * deliver a cache update to a query while that query is refetching, so the
   * Hub used to move only when the refetch landed. This way the decision moves
   * the page at once and the re-read brings in what the mutation doesn't
   * return, the activity row.
   *
   * Awaited, so the controls stay held until it lands. On a stale decision the
   * mutation changes nothing, and the controls are all that stands between the
   * approver and another request that can only fail. A failed re-read is the
   * host query's to show (the Hub's retry panel), not a failed decision.
   */
  async function reread() {
    try {
      await client.refetchQueries({ include: refetchQueries });
    } catch {
      // Surfaced by the host query's own error state.
    }
  }

  async function decide(decision: EngagementGovernanceDecision, extra: DecisionExtras = {}) {
    setError(null);
    setPending(decision);
    try {
      await submitDecision({
        variables: { input: { engagementId, decision, ...extra } },
        update: (cache) => dropFromQueue(cache, engagementId),
      });
    } catch (err) {
      const code = firstErrorCode(err);
      // Someone else already acted, so what this approver is looking at is
      // stale. Settle it like a decision rather than leave buttons that can
      // only fail again.
      if (code !== ERROR_CODES.ENGAGEMENT_AWAITING_MISMATCH) {
        setError(getDefaultErrorMessage(code ?? '') ?? 'That decision could not be recorded.');
        setPending(null);
        return;
      }
      dropFromQueue(client.cache, engagementId);
    }
    onDecided?.();
    await reread();
    setPending(null);
  }

  return { decide, pending, busy: pending !== null, error };
}
